/**
 * Every tuning value from GDD §4–6 (plus the §3/§7/§10 values the code needs) lives in config/gameplay.json.
 * tests/unit/config-vs-gdd.test.ts proves each value against a verbatim quote from docs/GDD.md.
 */
import raw from '../../config/gameplay.json';

export interface Range {
  min: number;
  max: number;
}
export interface HitBox {
  halfWidth: number;
  halfLength: number;
}
export interface Linear {
  base: number;
  perLevel: number;
}
export interface CameraProfile {
  fov: number;
  height: number;
  behind: number;
  lookAhead: number;
}
export interface Shooter {
  range: Range;
  side?: number;
  speed: number;
  spread: number;
}

export interface GameplayConfig {
  camera: {
    landscape: CameraProfile;
    portrait: CameraProfile;
    follow: { riverShare: number; riverAhead: number; jetShare: number; damping: number };
    breathe: { amp: number; rate: number };
  };
  render: {
    exposure: number;
    shadowMapMin: number;
    shadowHalfExtent: number;
    fog: { near: number; far: number };
    dpr: { fixedUpTo: number; adaptiveMin: number; adaptiveMax: number };
  };
  flight: {
    cruise: number;
    min: number;
    max: number;
    steerResponse: number;
    bankMax: number;
    lateralSpeed: number;
    sensitivity: Range;
    throttleEase: number;
    altitude: number;
  };
  guns: {
    interval: number;
    tracerSpeedBonus: number;
    tracerLife: number;
    bridgeHitsBase: number;
    bridgeHitsPerLevel: number;
  };
  fuel: { drainPerSec: number; refuelPerSec: number; lowThreshold: number };
  crash: {
    bankHeight: number;
    jetBox: HitBox;
    bridgeDz: number;
    tracerZone: { across: number; along: number };
    flakRadius: number;
  };
  lives: {
    start: number;
    extraEvery: number;
    max: number;
    wreckSec: number;
    invulnSec: number;
    checkpointPastBridge: number;
    gameOverSec: number;
  };
  score: { gunboat: number; heli: number; fuel: number; jet: number; bridge: number };
  enemies: {
    hitBox: { gunboat: HitBox; heli: HitBox; jet: HitBox; fuel: HitBox };
    heliAltitude: Range;
    jetAltitude: number;
    patrolSpeed: { gunboat: Linear; heli: Linear; jet: Linear };
    bankClearance: { gunboatExtra: number; heli: number };
    sleeperRange: number;
    sleeperShare: number;
    jetAhead: Range;
    jetExitBeyond: number;
  };
  firing: {
    heli: Shooter;
    gunboat: Shooter;
    bridgeGun: Shooter;
    cooldown: { base: number; perLevel: number; min: number; jitter: number };
    bridgeCooldown: { base: number; distanceDivisor: number; jitter: number };
    quietAfterRespawnSec: number;
  };
  spawning: {
    sectionLength: number;
    startPastBridge: number;
    minSpacing: number;
    islandClear: number;
    margin: { gunboat: number; fuel: number; heli: number };
  };
  difficulty: {
    gunboats: { base: number; perLevel: number; max: number };
    helis: { base: number; perLevel: number; max: number };
    fuel: { base: number; levelDivisor: number; min: number };
    jets: { fromLevel: number; base: number; levelDivisor: number; max: number };
    heliPatrol: { fromLevel: number; base: number; perLevel: number };
    gunboatPatrol: { fromLevel: number; base: number; perLevel: number };
    heliFire: { fromLevel: number; base: number; perLevelAbove: number };
    gunboatFlak: { fromLevel: number; base: number; perLevelAbove: number };
    bridgeGunsFromLevel: number;
  };
  banners: { normalSec: number; dangerSec: number; gameOverSec: number; gapSec: number };
  flow: { titleFadeSec: number };
  text: {
    levelPrefix: string;
    levelBanners: string[];
    levelBannerAfter: string;
    sections: string[];
    bridgeDestroyed: string;
    extraJet: string;
    gameOver: string;
    jetLostPrefix: string;
    jetLost: {
      riverbank: string;
      gunboat: string;
      heli: string;
      jet: string;
      bridge: string;
      heliFire: string;
      flak: string;
      bridgeGuns: string;
      fuel: string;
    };
  };
}

export const config: GameplayConfig = raw;
